import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { processFields, processInitialValues, processSchema } from 'utils/form';
import {
  createProduct,
  updateProduct,
  useProductFieldConfig,
} from 'queries/products';
import { useTradeChoices, useUnitChoices } from 'queries/company';
import { Layout } from 'components/Form';
import Button from 'components/Button';
import { actionLabel, readAxiosErr, titleCase } from 'utils/misc';
import SkeletonLoader, { ContentType } from 'components/SkeletonLoader';
import toast from 'react-hot-toast';

interface IFormValues {
  name: string;
  trade: number;
  color_choices: string[];
  unit?: number;
  default_estimate_unit?: number;
  retail_price?: number;
  lineitem_note?: string;
  // is_active: boolean;
}

type AppProps = {
  onSuccess: (...args: any) => any;
  productId?: string;
  initial?: Record<string, any>;
  prefix?: string;
};

const Form: React.FC<AppProps> = ({
  onSuccess,
  productId = '',
  initial = {},
  prefix = '',
}) => {
  const queryClient = useQueryClient();
  const [colorChoices, setColorChoices] = useState<string[]>([]);

  const { data: config, isLoading } = useProductFieldConfig({
    product: productId,
  });

  const createMutation = useMutation(
    (formData: any) => createProduct(formData),
    {
      onError: (err: any) => {
        toast.error(readAxiosErr(err));
      },
      onSuccess: async (res: any) => {
        await Promise.all([
          queryClient.invalidateQueries(['products']),
          queryClient.invalidateQueries(['product-cache']),
          queryClient.invalidateQueries(['products-upgrade']),
          queryClient.invalidateQueries(['products-discount']),
          queryClient.invalidateQueries(['all-products']),
          queryClient.invalidateQueries(['roof-products']),
          queryClient.invalidateQueries([
            'matorder-lineitems',
            'product-choices',
          ]),
        ]);
        onSuccess(res);
      },
    }
  );

  const updateMutation = useMutation(
    (formData: any) => updateProduct(productId, formData),
    {
      onError: (err: any) => {
        toast.error(readAxiosErr(err));
      },
      onSuccess: async (res: any) => {
        await Promise.all([
          queryClient.invalidateQueries(['products']),
          queryClient.invalidateQueries(['product-cache']),
          queryClient.invalidateQueries(['products-upgrade']),
          queryClient.invalidateQueries(['products-discount']),
          queryClient.invalidateQueries(['all-products']),
        ]);
        onSuccess(res);
      },
    }
  );

  const schema = yup.object(
    processSchema(
      {
        name: yup.string(),
        trade: yup.number(),
        color_choices: yup.array().of(yup.string()),
        unit: yup.number(),
        default_estimate_unit: yup.number(),
        retail_price: yup.number(),
        lineitem_note: yup.string(),
        // is_active: yup.boolean(),
      },
      config
    )
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues: initial,
  });

  useEffect(() => {
    setValue('color_choices', colorChoices);
  }, [colorChoices]);

  useEffect(() => {
    if (config) {
      const defaultValues = processInitialValues(config);
      const _colorChoices =
        defaultValues.color_choices
          ?.split(',')
          ?.map((c: string) => c.toLowerCase())
          ?.filter((c: string) => c) || [];
      setColorChoices(_colorChoices);
      reset({
        ...defaultValues,
        name: initial?.name || defaultValues.name,
        trade: initial?.trade || defaultValues.trade,
        color_choices: _colorChoices,
      });
    }
  }, [config]);

  const onSubmit: SubmitHandler<IFormValues> = async (formData) => {
    const payload = {
      ...formData,
      name: prefix ? `${prefix} ${formData.name}` : formData.name,
      color_choices: formData.color_choices?.join(','),
    };
    if (productId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const fields = [
    {
      type: FieldType.Input,
      label: 'Product Name',
      placeholder: 'Product Name',
      error: errors.name?.message,
      ...register('name'),
    },
    {
      name: 'trade',
      type: FieldType.AsyncSelect,
      label: 'Trade',
      placeholder: 'Trade',
      error: errors.trade?.message,
      control,
      optionsPromise: useTradeChoices,
    },
    {
      name: 'color_choices',
      type: FieldType.Multiselect,
      label: 'Color Choices',
      placeholder: 'Select Colors',
      isCreatable: true,
      // @ts-ignore
      error: errors.color_choices?.message,
      control,
      onCreate: (color: any) => {
        setColorChoices((c: any) => [...c, color.toLowerCase()]);
      },
      options: colorChoices.map((c) => ({
        label: c?.toLowerCase(),
        value: c?.toLowerCase(),
      })),
    },
    {
      name: 'unit',
      type: FieldType.AsyncSelect,
      label: 'Supplier Unit',
      placeholder: 'Unit',
      error: errors.unit?.message,
      control,
      isClearable: true,
      optionsPromise: useUnitChoices,
      info: 'Select the unit that you order this product in from your supplier.',
    },
    {
      name: 'default_estimate_unit',
      type: FieldType.AsyncSelect,
      label: 'Unit for Estimates / Bids',
      placeholder: 'Unit',
      error: errors.default_estimate_unit?.message,
      control,
      isClearable: true,
      optionsPromise: useUnitChoices,
    },
    {
      name: 'retail_price',
      type: FieldType.Number,
      label: 'Default Unit Price',
      placeholder: '0',
      error: errors.retail_price?.message,
      control,
      info: (
        <div>
          When you are creating an estimate from scratch (not from a roof
          template), <br />
          Rooflink will default this product to the price you enter here.
        </div>
      ),
    },
    {
      type: FieldType.TextArea,
      label: 'Note',
      placeholder: 'Add note',
      error: errors.lineitem_note?.message,
      ...register('lineitem_note'),
    },
  ];

  if (isLoading) {
    return <SkeletonLoader contentType={ContentType.Form} size={4} />;
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Layout
          fields={processFields(fields, config)}
          loading={createMutation.isLoading || updateMutation.isLoading}
        />

        <Button
          label={actionLabel(
            productId,
            'Product',
            createMutation.isLoading || updateMutation.isLoading
          )}
          className="mt-8"
          loading={createMutation.isLoading || updateMutation.isLoading}
        />
      </form>
    </div>
  );
};

export default Form;
